import type { Cart, CartItem, Category, OptionGroup } from "@/types";
import type { CheckoutCartItem, CheckoutCartItemOption } from "@/types/external/order-request";
import { allocateCents, computeItemTotal, priceGroupTotal, roundCurrency, sanitizeMoney } from "@/lib/pricing";
import { findCategoryForOffer } from "./find-category-for-offer";
import {
  getPortionTarget,
  isPortionOptionGroup,
  resolveEffectivePortionQuantities,
  toPortionFractions,
} from "./option-group-portions";

type CartItemOptionPayload = Omit<CheckoutCartItemOption, "categoryId" | "category">;

const FALLBACK_INDEX = Number.MAX_SAFE_INTEGER;

type GroupLine = { unitPrice: number; originalPrice: number; totalPrice: number };

/**
 * OpenDelivery breakdown of a group billed as a whole (a portion group, or any
 * AVG/MAX group): each option's billed unit price and its share of the group total.
 */
function buildGroupLines(
  optionGroup: OptionGroup,
  effectiveQuantities: Record<string, number>,
): Map<string, GroupLine> {
  const selected = (optionGroup.options ?? []).filter((option) => (effectiveQuantities[option.id] ?? 0) > 0);
  const topOption = selected.reduce<(typeof selected)[number] | undefined>(
    (top, option) => (!top || sanitizeMoney(option.price.value) > sanitizeMoney(top.price.value) ? option : top),
    undefined,
  );
  const billedOption = (option: (typeof selected)[number]) =>
    optionGroup.priceMethod === "MAX" && topOption ? topOption : option;
  const groupCents = Math.round(roundCurrency(priceGroupTotal(optionGroup, effectiveQuantities)) * 100);
  const shares = allocateCents(
    groupCents,
    selected.map((option) => sanitizeMoney(billedOption(option).price.value) * effectiveQuantities[option.id]),
  );
  return new Map(
    selected.map((option, index) => {
      const shown = billedOption(option);
      const unitPrice = sanitizeMoney(shown.price.value);
      return [
        option.id,
        {
          unitPrice: roundCurrency(unitPrice),
          originalPrice: roundCurrency(shown.price.originalValue ?? unitPrice),
          totalPrice: shares[index] / 100,
        },
      ];
    }),
  );
}

function billsAsWhole(optionGroup: OptionGroup | undefined, portionTarget: number | null): optionGroup is OptionGroup {
  return Boolean(optionGroup && (portionTarget || optionGroup.priceMethod === "AVG" || optionGroup.priceMethod === "MAX"));
}

/**
 * Builds the options payload for a single cart item: zero-quantity filtering,
 * per-option special instructions, the fraction of the item each portion option
 * covers (1/2 = 0.5), and each option of a portion or AVG/MAX group priced as a share of the group.
 *
 * Options are emitted in catalog order — option groups by `OptionGroup.index`,
 * then each group's options by `Option.index` — instead of the order the
 * customer happened to tap them, so the order payload (and every downstream
 * view of it) mirrors the menu sequence.
 *
 * `categoryId`/`categoryName` are optional on purpose: the checkout payload
 * includes the category id + name on each option, while the cart-sync backend
 * payload omits both — passing them (or not) keeps each caller's wire shape
 * unchanged. The remaining display fields (name, externalCode, unit, imageUrl,
 * isPortionable) are always emitted: harmless for cart-sync, required by
 * checkout now that the merchant backend no longer hydrates item display data.
 */
export function buildCartItemOptionsPayload(cartItem: CartItem): CartItemOptionPayload[];
export function buildCartItemOptionsPayload(cartItem: CartItem, categoryId: string, categoryName: string): CheckoutCartItemOption[];
export function buildCartItemOptionsPayload(cartItem: CartItem, categoryId?: string, categoryName?: string): CartItemOptionPayload[] {
  const optionNotes = cartItem.optionSpecialInstructions ?? {};

  return Object.entries(cartItem.optionQuantities)
    .map(([optionGroupId, optionQuantities]) => ({
      optionGroupId,
      optionQuantities,
      optionGroup: cartItem.optionGroups.find((group) => group.id === optionGroupId),
    }))
    .sort((a, b) => (a.optionGroup?.index ?? FALLBACK_INDEX) - (b.optionGroup?.index ?? FALLBACK_INDEX))
    .flatMap(({ optionGroupId, optionQuantities, optionGroup }) => {
      const portionTarget = optionGroup && isPortionOptionGroup(optionGroup)
        ? getPortionTarget(optionGroup) ?? optionGroup.maxPermitted
        : null;
      const normalizedQuantities = optionGroup && portionTarget
        ? resolveEffectivePortionQuantities(optionQuantities, portionTarget)
        : optionQuantities;
      const groupLines = billsAsWhole(optionGroup, portionTarget)
        ? buildGroupLines(optionGroup, normalizedQuantities)
        : null;
      const portionFractions = portionTarget ? toPortionFractions(normalizedQuantities, portionTarget) : null;

      const optionIndex = (optionId: string) =>
        optionGroup?.options?.find((option) => option.id === optionId)?.index ?? FALLBACK_INDEX;

      return Object.entries(normalizedQuantities)
        .filter(([, quantity]) => quantity > 0)
        .sort(([a], [b]) => optionIndex(a) - optionIndex(b))
        .map(([optionId, quantity]) => {
          const option = optionGroup?.options?.find((o) => o.id === optionId);
          const optionItem = cartItem.itemOptions[optionId];
          const groupLine = groupLines?.get(optionId);
          const optionUnitValue = groupLine?.unitPrice ?? option?.price.value ?? 0;
          const optionSubtotal = groupLine?.totalPrice ?? roundCurrency(optionUnitValue * quantity);
          const optionCatalogId = optionItem?.id ?? option?.itemId ?? "";

          return {
            id: optionCatalogId,
            optionGroupId,
            ...(categoryId !== undefined ? { categoryId, category: categoryName ?? "" } : {}),
            optionId,
            quantity: portionFractions?.[optionId] ?? quantity,
            specialInstructions: optionNotes[optionGroupId]?.[optionId] ?? "",
            name: optionItem?.name ?? "",
            externalCode: optionItem?.externalCode ?? "",
            unit: optionItem?.unit ?? "",
            imageUrl: optionItem?.image?.URL ?? "",
            isPortionable: optionGroup ? isPortionOptionGroup(optionGroup) : false,
            unitPrice: { value: optionUnitValue, currency: "BRL" },
            originalPrice: {
              value: groupLine?.originalPrice ?? option?.price.originalValue ?? optionUnitValue,
              currency: "BRL",
            },
            subtotalPrice: { value: optionSubtotal, currency: "BRL" },
            totalPrice: { value: optionSubtotal, currency: "BRL" },
          };
        });
    });
}

function buildCheckoutCartItems(cart: Cart, categories: Category[]): CheckoutCartItem[] {
  return cart.items.map((cartItem) => {
    const category = findCategoryForOffer(categories, cartItem.itemOffer.id);
    const specialInstructions = cartItem.specialInstructions || "";

    const baseValue = cartItem.itemOffer.price.value;
    const optionsPerUnit = roundCurrency(
      computeItemTotal(cartItem.itemOffer, cartItem.optionGroups, cartItem.optionQuantities) -
        sanitizeMoney(cartItem.itemOffer.price.value),
    );

    const catalogItemId = cartItem.item?.id || cartItem.itemOffer?.itemId || "";

    return {
      id: catalogItemId,
      itemOfferId: cartItem.itemOffer.id,
      categoryId: category?.id ?? "",
      quantity: cartItem.quantity,
      specialInstructions,
      name: cartItem.item.name,
      externalCode: cartItem.item.externalCode,
      unit: cartItem.item.unit ?? "",
      imageUrl: cartItem.item.image?.URL ?? "",
      category: category?.name ?? "",
      unitPrice: { value: baseValue, currency: "BRL" },
      originalPrice: {
        value: cartItem.itemOffer.price.originalValue ?? baseValue,
        currency: "BRL",
      },
      optionsPrice: { value: optionsPerUnit, currency: "BRL" },
      subtotalPrice: { value: roundCurrency(baseValue * cartItem.quantity), currency: "BRL" },
      totalPrice: { value: cartItem.itemTotalPrice, currency: "BRL" },
      options: buildCartItemOptionsPayload(cartItem, category?.id ?? "", category?.name ?? ""),
    };
  });
}

export { buildCheckoutCartItems };
